import { API_URL } from "@/constants/environment";
import axios from "axios";

class CustomerAuthService {
  // Constructor
  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      timeout: 10000,
    });

    // Request Interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token")    
        if (token) {
          config.headers["Authorization"] = `Bearer ${token}`;
        } else {
          config.headers["Authorization"] = null;
        }
    
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log("Unauthorized request");
        }
        return Promise.reject(error);
      }
    );
  }

  // Register customer
  async registerCustomer(formData) {
    try {
      const response = await axios.post(
        `${API_URL}/customers/register`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // Required for FormData
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Login customer
  async loginCustomer(email, password) {
    try {
      const response = await axios.post(`${API_URL}/customers/login`, { email, password });
      return response?.data;
    } catch (error) {
      throw error.response;
    }
  }

  async googleLogin(idToken) {
    try {
      const response = await axios.post(`${API_URL}/customers/google-login`, { idToken });
      return response?.data;
    } catch (error) {
      throw error.response;
    }
  }


  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await axios.post(`${API_URL}/customers/forgot-password`, { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verify OTP
  async verifyForgetPasswordOtp(email, otp) {
    try {
      const response = await axios.post(`${API_URL}/customers/verify-otp`, { email, otp });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Reset password
  async resetPassword(email, newPassword) {
    try {
      const response = await axios.post(`${API_URL}/customers/reset-password`, { email, newPassword });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Fetch user data
  async fetchUserData(userId, token = null) {
    try {
      if (!userId) throw new Error("User ID missing");

      const response = await axios.get(`${API_URL}/customers/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`, // Ensure token is passed correctly
        },
      });

      return response.data.data?.data || null; // Handle nested response
    } catch (err) {
      throw new Error(err.message || "Failed to fetch user data");
    }
  }

  // Update user data
  async updateUserData(userId, data) {
    try {
      const access_token = localStorage.getItem("access_token");
      const response = await axios.put(`${API_URL}/customers/${userId}`, data, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      return response.data;
    } catch (err) {
      throw new Error(err.message || "Failed to update user data");
    }
  }

  // Resend email OTP
  async resendEmailOTP(email) {
    try {
      const response = await axios.post(`${API_URL}/customers/resend-email-otp`, { email });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Verify email OTP
  async verifyEmailOTP(email, otp) {
    try {
      const response = await axios.post(`${API_URL}/customers/verify-email-otp`, { email, otp });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Get All FAQs
  async getFaqs(){
    try {
      const response = await axios.get(`${API_URL}/faq/active`);
      return response.data;
    } catch (error) {
      console.error("Error fetching FAQs:", error);
      throw error;
    }
  };
}

// Instantiate the service
const customerAuthService = new CustomerAuthService();
export default customerAuthService;
